import { useQuery } from '@tanstack/react-query';
import {
  RECEIVE_TYPE_OPTIONS,
  SERVICE_CATEGORY_OPTIONS,
  SERVICE_DISCOUNT_OPTIONS,
} from '@/shared/constants/users';
import { fetchClassifications } from './api';
import { CheckField, SelectField, TextField, type ProfileFormApi } from './ProfileInputs';
import { MAX_PRIORITY_ORDER, isServiceDiscountOn, type ProfileMode, type ProfilePermissions } from './profileForm';

type Props = {
  form: ProfileFormApi;
  mode: ProfileMode;
  permissions: ProfilePermissions;
};

const REQUIRED = <span className="text-red">*</span>;

const digitsOnly = (value: string) => value.replace(/\D/g, '');

/** Service-role fields that only exist on the edit page. */
const EditServiceFields = ({ form, permissions }: Omit<Props, 'mode'>) => {
  const { values } = form;
  const classifications = useQuery({ queryKey: ['classifications'], queryFn: fetchClassifications });
  const isDiscountOn = isServiceDiscountOn(values);

  return (
    <>
      <SelectField
        form={form}
        field="classification"
        errorKey="classification"
        label="Звёздность"
        options={classifications.data ?? []}
        isLoading={classifications.isLoading}
        disabled={!permissions.canChangeClassification}
      />
      <TextField form={form} field="rate" errorKey="rate" label="Коэффициент для работ" />
      <CheckField form={form} field="isFranchise" errorKey="is_franchise" label="Официальный партнёр" />
      <CheckField
        form={form}
        field="isCostWorkDumpingOff"
        errorKey="is_cost_work_dumping_off"
        label="Отключить систему антидемпинга"
        disabled={!permissions.canEditDumpingOff}
      />
      <CheckField
        form={form}
        field="canWorkWithAutopark"
        errorKey="can_work_with_autopark"
        label="Работа с автопарками"
        disabled={!permissions.canEditWorkWithAutopark}
      />
      <CheckField
        form={form}
        field="payOnline"
        errorKey="pay_online"
        label="Принимает безналичную оплату"
        disabled={!permissions.canChangePayOnline}
      />
      <TextField form={form} field="contactPerson" errorKey="contact_person" label="ЕОСАГО контактное лицо" />
      <TextField form={form} field="contactPersonPhone" errorKey="contact_person_phone" label="ЕОСАГО телефон для связи" />
      <CheckField form={form} field="isPriority" errorKey="is_priority" label="Приоритетное СТО" />
      <TextField
        form={form}
        field="priorityOrder"
        errorKey="priority_order"
        label={`Порядок внутри приоритетных (1-${MAX_PRIORITY_ORDER})`}
        disabled={!values.isPriority}
        onValueChange={(value) => {
          const digits = digitsOnly(value);
          return Number(digits) <= MAX_PRIORITY_ORDER ? digits : null;
        }}
      />
      <SelectField
        form={form}
        field="serviceDiscountId"
        errorKey="discount_id"
        label="Тип лояльности"
        options={SERVICE_DISCOUNT_OPTIONS}
        clearable
      />
      <TextField
        form={form}
        field="serviceDiscountPercent"
        errorKey="discount_percent"
        label={<>Процент {isDiscountOn && REQUIRED}</>}
        onValueChange={digitsOnly}
      />
      <CheckField
        form={form}
        field="tgEnabled"
        errorKey="tg_enabled"
        label="Telegram-уведомления"
        disabled={!values.tgEnabled}
        hint={
          values.tgEnabled
            ? 'Подключены (снимите галочку и сохраните, чтобы отключить)'
            : 'Не подключены (подключение — в старой админке)'
        }
      />
    </>
  );
};

/** Service-role fields: type, receive type and B2B on both pages, everything else on edit. */
export const ServiceProfileFields = ({ form, mode, permissions }: Props) => (
  <>
    <SelectField form={form} field="category" errorKey="category" label="Тип СТО" options={SERVICE_CATEGORY_OPTIONS} />
    <SelectField
      form={form}
      field="receiveType"
      errorKey="receive_type"
      label="Способ получения заявок"
      options={RECEIVE_TYPE_OPTIONS}
    />
    <CheckField form={form} field="acceptB2b" errorKey="accept_b2b" label="Принимает заявки от юр. лиц" />
    {mode === 'edit' && <EditServiceFields form={form} permissions={permissions} />}
  </>
);
